import React, { useEffect, useState } from "react";
import { Link, graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import LocationMark from "../components/icons/Locations/LocationMark";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import "../../scss/main.scss";
import BrochureRequestPanel from "../components/BrochureRequestPanel";
import TrustpilotInline from "../components/TrustpilotInline";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";

const londonCarePaths = [
	["01", "Dementia care", "/condition/dementia/"],
	["02", "Parkinson's care", "/condition/parkinsons/"],
	["03", "Multiple sclerosis care", "/condition/multiple-sclerosis/"],
	["04", "Cancer care", "/condition/cancer/"],
	["05", "Stroke care", "/condition/stroke/"],
	["06", "Care for couples", "/couples-care/"],
];

const londonCareJourney = [
	{
		number: "01",
		title: "Start with a conversation",
		text:
			"A Care Advisor listens to what is changing, what matters at home and what you want support to make possible.",
	},
	{
		number: "02",
		title: "Build the right plan",
		text:
			"A Care Manager gets to know the person, their routines, health needs and home before carefully matching suitable carers.",
	},
	{
		number: "03",
		title: "Stay supported",
		text:
			"Your dedicated Edyn team manages the practical detail, checks in regularly and adapts care as life changes.",
	},
];

const londonCareBenefits = [
	{
		title: "Familiar surroundings",
		text:
			"The home, garden, pets and possessions that hold a lifetime of meaning all stay close.",
	},
	{
		title: "A personal rhythm",
		text:
			"Meals, visitors, appointments and bedtimes can continue around the person rather than a shared timetable.",
	},
	{
		title: "One-to-one attention",
		text:
			"A carefully matched carer gets to know the small details that make support feel genuinely personal.",
	},
];

const londonLocalLife = [
	{
		image: "locations-landing-image.jpg",
		title: "Familiar streets",
		location: "Around the neighbourhood",
		alt: "A quiet residential mews in London",
	},
	{
		image: "ec-img-1.png",
		title: "Time outdoors",
		location: "Walks and fresh air",
		alt: "An older couple walking together in a leafy garden",
	},
	{
		image: "ec-img-3.png",
		title: "Everyday interests",
		location: "At home or close by",
		alt: "Books and flowers arranged in a garden",
	},
	{
		image: "carer-helping-with-haircut.png",
		title: "Practical support",
		location: "Personal care, your way",
		alt: "A carer helping an older man with his hair",
	},
];

const regionalIntroductions = {
	"south-east":
		"From familiar high streets and villages to coastal paths and family nearby, home is part of a wider life across the South East.",
	"south-west":
		"From market towns and coastal communities to neighbours and family nearby, home is part of a wider life across the South West.",
	"east-midlands":
		"Across market towns, villages and city neighbourhoods, familiar routines keep people connected to life in the East Midlands.",
	"west-midlands":
		"Across city neighbourhoods, market towns and close-knit communities, familiar routines keep people connected to life in the West Midlands.",
	"west-of-england":
		"From Bristol neighbourhoods to surrounding towns and villages, familiar places and people shape everyday life across the West of England.",
	"north-east":
		"From coastal communities to city neighbourhoods and market towns, familiar places and people shape everyday life across the North East.",
	"north-west":
		"From busy city neighbourhoods to market towns and coastal communities, familiar routines keep people connected across the North West.",
};

const regionalPlanning = {
	"south-east": {
		title: "Planning care around South East routines.",
		copy: [
			"Life in the South East can span villages, commuter towns and larger centres. A useful care plan starts with the real geography of the week: where appointments take place, which relatives visit, and which familiar journeys are worth protecting.",
			"The local examples above, from RHS Wisley and Ripley Farmers' Market to community activities and Royal Berkshire Hospital, show how different those routines can be. They are prompts for a conversation, not a fixed programme: each person decides what a good week should include.",
		],
	},
	"south-west": {
		title: "Keeping distance from disrupting daily life.",
		copy: [
			"In the South West, family, healthcare and favourite places may be spread across towns, villages and rural roads. Planning therefore includes the practical detail: transport, appointment times, shopping and how a carer can help someone stay connected without rushing the day.",
			"Places such as the Eden Project, Devon farm shops and local card clubs illustrate the range of outings a person may value. The aim is not to fill a timetable, but to keep chosen interests possible alongside support at home and visits to services such as Chippenham Community Hospital.",
		],
	},
	"east-midlands": {
		title: "Care shaped around market-town life.",
		copy: [
			"Across the East Midlands, a weekly rhythm may combine a nearby market, visits from family, clubs and healthcare in another town. Mapping those details early helps the care plan support both independence at home and the journeys that make the week feel familiar.",
			"Newark Royal Market, Crich Tramway Village, card clubs and Newark Hospital are examples of very different local touchpoints. A carer can support the ones that matter to the individual, while the wider Edyn team keeps practical arrangements and changing care needs connected.",
		],
	},
	"west-midlands": {
		title: "A care plan for city and town routines.",
		copy: [
			"The West Midlands brings together dense city neighbourhoods, quieter suburbs and market towns. Good planning looks beyond tasks in the home to understand transport, family visits, preferred shops and the times of day when local routines matter most.",
			"Winterbourne Botanic Garden, Stratford-upon-Avon, art clubs and Sandwell Hospital offer examples of leisure, community and healthcare journeys. They help families discuss what should remain part of life, while care is adjusted to the person's abilities and priorities.",
		],
	},
	"west-of-england": {
		title: "Connecting home with the wider community.",
		copy: [
			"In the West of England, day-to-day life may move between Bristol, surrounding towns and nearby villages. A personal plan records the meaningful details: regular visitors, familiar routes, community groups and how appointments are reached.",
			"Gloucester Cathedral, SS Great Britain, bowling clubs and Weston General Hospital show the varied settings that can sit within one region. The relevant places will be different for every family; care is built around their choices rather than a standard list of activities.",
		],
	},
	"north-east": {
		title: "Protecting ties to coast, city and community.",
		copy: [
			"North East routines often cross distinct coastal and city communities. When care is arranged, it helps to record the practical links between home, relatives, health services and the places where someone feels known, then plan support around those connections.",
			"The National Glass Centre in Sunderland, Tynemouth Market, computer classes and Blyth Community Hospital are examples of cultural, social and healthcare touchpoints. They prompt a more useful question: which parts of local life does this person want help to continue?",
		],
	},
	"north-west": {
		title: "Making room for the journeys that matter.",
		copy: [
			"Across the North West, a person's network may stretch between neighbourhoods, towns and cities. Care planning can account for those journeys, from a regular club or market to family visits and clinical appointments, while keeping the home as a stable base.",
			"A Mersey ferry trip, Macclesfield's Treacle Market, a chess club and Aintree University Hospital represent different reasons to leave home. The plan focuses only on what matters to the person and can change as their confidence, mobility or health needs change.",
		],
	},
};

const londonRegionalTeam = [
	{
		number: "01",
		name: "Daniel Greeves",
		role: "Regional Care Advisor",
		image: "daniel-new.png",
		headline: "Makes the first steps feel clear",
		text:
			"Listens first, explains the options clearly and helps families understand the most useful next step.",
	},
	{
		number: "02",
		name: "Muna Khamdun",
		role: "Regional Care Manager",
		image: "muna.png",
		headline: "Shapes and reviews the care plan",
		text:
			"Oversees the care plan, reviews changing needs and keeps quality accountable throughout the placement.",
	},
	{
		number: "03",
		name: "Sarah van der Velden",
		role: "Regional Care Coordinator",
		image: "sarah.png",
		headline: "Keeps every detail connected",
		text:
			"Keeps schedules, communication and practical details joined up once care is underway.",
	},
];

export default function Location({ data }) {
	const {
		airtableLocations,
		site: {
			siteMetadata: {
				appConfig: { gtmContainerId },
			},
		},
	} = data;
	const {
		data: { Splash_image_sharp, Name: locationName },
	} = airtableLocations;
	const [
		activity1,
		activity2,
		activity3,
		activity4,
	] = airtableLocations.data.activities.map((res) => res.data);
	const splashImage = getImage(Splash_image_sharp);
	const activityImage1 = getImage(activity1.image_sharp);
	const activityImage2 = getImage(activity2.image_sharp);
	const activityImage3 = getImage(activity3.image_sharp);
	const activityImage4 = getImage(activity4.image_sharp);
	const formattedLocationName = locationName.toLowerCase().startsWith("the ")
		? locationName.substring(4)
		: locationName;
	const locationSlug = airtableLocations.data.slug;
	const isLondon = locationSlug === "london";
	const locationNameInSentence = isLondon
		? locationName
		: locationName.toLowerCase().startsWith("the ")
		? locationName
		: `the ${locationName}`;
	const regionalIntroduction =
		regionalIntroductions[locationSlug] ||
		`Familiar places, nearby family and everyday routines keep people connected to life in ${locationNameInSentence}.`;
	const localPlanning = regionalPlanning[locationSlug];
	const locationLocalLife = isLondon
		? londonLocalLife.map((activity) => ({ ...activity, isStatic: true }))
		: [
				[activity1, activityImage1],
				[activity2, activityImage2],
				[activity3, activityImage3],
				[activity4, activityImage4],
		  ].map(([activity, image]) => ({
				image,
				title: activity.Activity_description,
				location: activity.Location_name,
				alt: `${activity.Activity_description} ${activity.Location_name}`,
				isStatic: false,
		  }));
	const [ctaUrl, setCtaUrl] = useState("/care-consultation/");
	useEffect(() => {
		setCtaUrl(`/care-consultation/${document.location.search}`);
	}, []);

	return (
		<>
			<Seo
				title={`Live-in Care in ${formattedLocationName} | Edyn Care`}
				description={`Fully managed live-in care in ${formattedLocationName}, with carefully selected professional carers and an experienced Edyn care team supporting every placement.`}
				path={`/location/${airtableLocations.data.slug}/`}
				service={{
					name: `Live-in care in ${formattedLocationName}`,
					areaServed: [formattedLocationName],
				}}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{
						name: formattedLocationName,
						path: `/location/${airtableLocations.data.slug}/`,
					},
				]}
			/>
			<div className="container edyn-redesign-shell">
				<Header />
				<main
					id="main-content"
					className={`edyn-london${isLondon ? "" : " edyn-location-redesign"}`}
				>
					<section className="london-hero">
						<div className="site-shell london-hero__panel">
							<div className="london-hero__copy">
								<p className="eyebrow eyebrow--light">
									Live-in care in {locationNameInSentence}
								</p>
								<h1>
									<span>Care at home.</span>{" "}
									<em>{isLondon ? "London life." : "Life, locally."}</em>
								</h1>
								<p className="london-hero__lead">
									One-to-one live-in care that keeps the person, their home and
									the life they know in {locationNameInSentence} at the centre
									of every day.
								</p>
								<div className="button-row">
									<Link className="button button--cream" to={ctaUrl}>
										Arrange a care consultation
									</Link>
									<a className="button button--text-light" href="#local-life">
										Explore care in {locationNameInSentence}{" "}
										<span aria-hidden="true">&#8595;</span>
									</a>
								</div>
								<TrustpilotInline className="london-hero__trust" />
							</div>

							<div className="london-hero__visual">
								<GatsbyImage
									className="london-hero__image"
									image={splashImage}
									alt={`A familiar place in ${formattedLocationName}`}
								/>
								<div className="london-hero__location">
									<LocationMark className="icon-default" />
								<p>
									<strong>Care that stays local</strong>
									{airtableLocations.data.Splash_Image_location_description}
								</p>
								</div>
							</div>
						</div>
					</section>

					<section className="london-proof" aria-label="Edyn care assurances">
						<div className="site-shell london-proof__grid">
							<p>
								<strong>CQC regulated</strong>
								<span>Fully managed live-in care</span>
							</p>
							<p>
								<strong>{isLondon ? "London-wide" : "Regional"} support</strong>
								<span>Care shaped around local life</span>
							</p>
							<p>
								<strong>Professional carers</strong>
								<span>Only 2% of applicants are accepted and trained</span>
							</p>
						</div>
					</section>

					<section className="london-intro section-space">
						<div className="site-shell london-intro__grid">
							<p className="eyebrow">Care that belongs at home</p>
							<div>
								<h2>
									Keep the neighbourhood, the people and the life that already
									matter.
								</h2>
								<div className="london-intro__copy">
									<p>
										{isLondon
											? "London is not one place. It is the familiar corner shop, the park at the end of the road, neighbours, family and routines built over years."
											: regionalIntroduction}
									</p>
								<p>
									Live-in care brings professional support into that world, so
										daily life can remain recognisable while needs are met
										safely and thoughtfully.
									</p>
								</div>
							</div>
						</div>
					</section>

					<section
						id="local-life"
						className="london-life section-space section-soft"
					>
						<div className="site-shell">
							<div className="section-heading section-heading--split">
								<div>
									<p className="eyebrow">Life stays local</p>
									<h2>
										{isLondon
											? "Care that keeps London within reach."
											: `Care that keeps life in ${locationNameInSentence} within reach.`}
									</h2>
								</div>
								<p>
									A live-in carer can support familiar outings, appointments and
									everyday moments, as well as personal care at home.
								</p>
								{(isLondon || locationSlug === "south-east") && (
									<p>
										See our focused guide to{" "}
										<Link to="/live-in-care-south-east-london/">live-in care across the South East and London</Link>{" "}
										for care options, pricing and the local team.
									</p>
								)}
							</div>

							<div className="london-life__grid">
								{locationLocalLife.map((activity, index) => (
									<figure
										className="london-life__card"
										key={`${activity.title}-${index}`}
									>
										{activity.isStatic && activity.image === "carer-helping-with-haircut.png" ? (
											<picture>
												<source srcSet="/assets/marketing-site/images/carer-helping-with-haircut-800.webp 800w, /assets/marketing-site/images/carer-helping-with-haircut-1600.webp 1600w" sizes="(max-width: 700px) 100vw, 50vw" type="image/webp" />
												<img src="/assets/marketing-site/images/carer-helping-with-haircut-800.webp" alt={activity.alt} width="800" height="731" loading="lazy" />
											</picture>
										) : activity.isStatic ? (
											<img
												src={`/assets/marketing-site/images/${activity.image}`}
												alt={activity.alt}
												loading="lazy"
											/>
										) : (
											<GatsbyImage image={activity.image} alt={activity.alt} />
										)}
										<figcaption>
											<span aria-hidden="true">0{index + 1}</span>
											<div>
												<strong>{activity.title}</strong>
												<small>{activity.location}</small>
											</div>
										</figcaption>
									</figure>
									))}
							</div>
							<div
								id="life-local-reviews"
								className="london-life__reviews"
							>
								<p>
									Families can also read independent feedback from people who
									have arranged care with Edyn.
								</p>
								<MicroCombo
									classNames="london-life__trustpilot london-life__trustpilot--desktop"
									alignment="right"
									width="270px"
								/>
								<MicroCombo
									classNames="london-life__trustpilot london-life__trustpilot--mobile"
									alignment="left"
									width="270px"
								/>
							</div>
						</div>
						</section>

						{localPlanning && (
							<section className="location-planning section-space" aria-labelledby="location-planning-title">
								<div className="site-shell location-planning__grid">
									<div>
										<p className="eyebrow">Planning locally</p>
										<h2 id="location-planning-title">{localPlanning.title}</h2>
									</div>
									<div className="location-planning__copy">
										{localPlanning.copy.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
									</div>
								</div>
							</section>
						)}

						<section
						id="london-specialist"
						className="london-specialist section-space"
					>
						<div className="site-shell">
							<div className="section-heading section-heading--split">
								<div>
									<p className="eyebrow">Specialist support</p>
									<h2>Experience for changing and complex needs.</h2>
								</div>
								<p>
									Each plan is personal. Our care teams can support a range of
									conditions while protecting independence, dignity and the
									person's own way of life.
								</p>
							</div>

							<div className="london-specialist__grid">
								{londonCarePaths.map(([number, title, url]) => (
									<Link to={url} key={title}>
										<span>{number}</span>
										<strong>{title}</strong>
										<i aria-hidden="true">&#8599;</i>
									</Link>
								))}
							</div>
						</div>
					</section>

					<section
						id="london-journey"
						className="london-journey section-space section-dark"
					>
						<div className="site-shell">
							<div className="section-heading section-heading--split">
								<div>
									<p className="eyebrow eyebrow--light">How Edyn works</p>
									<h2>
										Clear steps. Familiar people. Care that keeps adapting.
									</h2>
								</div>
								<p>
									You will know who is responsible, what happens next and who to
									call whenever something changes.
								</p>
							</div>

							<ol className="london-journey__steps">
								{londonCareJourney.map((step) => (
									<li key={step.number}>
										<span>{step.number}</span>
										<h3>{step.title}</h3>
										<p>{step.text}</p>
									</li>
								))}
							</ol>

							<div className="london-journey__action">
								<Link className="button button--cream" to="/approach/">
									See how Edyn works
								</Link>
								<a className="button button--text-light" href="tel:02039709900">
									Call 020 3970 9900
								</a>
							</div>
						</div>
					</section>

					<section className="london-benefits section-space">
						<div className="site-shell london-benefits__grid">
							<div className="london-benefits__heading">
								<p className="eyebrow">Why live-in care?</p>
								<h2>More of home. More of the life around it.</h2>
								<p>
									Professional support can increase without asking someone to
									leave the place where they feel most themselves.
								</p>
							</div>
							<div className="london-benefits__list">
								{londonCareBenefits.map((benefit, index) => (
									<article key={benefit.title}>
										<span aria-hidden="true">0{index + 1}</span>
										<div>
											<h3>{benefit.title}</h3>
											<p>{benefit.text}</p>
										</div>
									</article>
								))}
							</div>
						</div>
					</section>

					<section className="london-brochure section-space section-clay">
						<div className="site-shell">
							<BrochureRequestPanel
								className="london-brochure__panel"
								gtmContainerId={gtmContainerId}
								headingLevel="h3"
								heading="Send me the Live-in guide"
								description="Tell us a little about your situation and we will email the guide straight to you."
								includeCareFor
								editorialIntro={
									<div className="london-brochure__intro">
										<p className="eyebrow eyebrow--light">
											Your free {formattedLocationName} care guide
										</p>
										<h2>A clearer way to plan care at home.</h2>
										<p>
											Read it in your own time, share it with family and use it
											to make the next conversation more useful.
										</p>
										<ul>
											<li>How fully managed live-in care works</li>
											<li>What to consider when comparing options</li>
											<li>Questions to ask before care begins</li>
										</ul>
										<div className="london-brochure__note">
											<span aria-hidden="true">PDF</span>
											<p>
												<strong>Delivered by email.</strong>
												No obligation and no pressure to decide today.
											</p>
										</div>
									</div>
								}
							/>
						</div>
					</section>

					<section className="london-team section-space section-dark">
						<div className="site-shell">
							<div className="section-heading section-heading--split">
								<div>
									<p className="eyebrow eyebrow--light">
										Your regional care team
									</p>
									<h2>Three people around every family.</h2>
								</div>
								<p>
									Advice, clinical oversight and practical coordination stay
									connected, so families always know who is responsible.
								</p>
							</div>

							<div
								className="london-team__roster"
								aria-label={`The regional ${formattedLocationName} care team`}
							>
								{londonRegionalTeam.map((member) => (
									<article className="london-team__member" key={member.role}>
										<div className="london-team__member-meta">
											<span aria-hidden="true">{member.number}</span>
											<p>{member.role}</p>
										</div>
										<div className="london-team__member-body">
											<figure>
												<div className="london-team__portrait">
													<img
														src={`/assets/marketing-site/images/team/${member.image}`}
														alt={member.role}
														width="873"
														height="1044"
														loading="lazy"
													/>
												</div>
											</figure>
											<div className="london-team__member-copy">
												<h3>{member.headline}</h3>
												<p>{member.text}</p>
											</div>
										</div>
									</article>
								))}
							</div>

							<div className="london-team__cta">
								<p>
									<strong>Not sure where to begin?</strong>
									Start with a relaxed conversation about what is changing at
									home.
								</p>
								<div className="button-row">
									<Link className="button button--cream" to={ctaUrl}>
										Talk to a Regional Care Advisor
									</Link>
									<a
										className="button button--text-light"
										href="tel:02039709900"
									>
										Call 020 3970 9900
									</a>
								</div>
							</div>
						</div>
					</section>
				</main>
				<Footer includeCTA={false} />
			</div>
		</>
	);
}

export const pageQuery = graphql`
	query($slug: String!) {
		site {
			siteMetadata {
				appConfig {
					gtmContainerId
				}
			}
		}
		airtableLocations(data: { slug: { eq: $slug } }) {
			data {
				slug
				Name
				Splash_image_sharp {
					childImageSharp {
						gatsbyImageData(
							width: 1200
							quality: 90
							placeholder: BLURRED
							formats: [AUTO, WEBP, AVIF]
						)
					}
				}
				Splash_Image_location_description
				activities {
					data {
						Activity_description
						image_sharp {
							childImageSharp {
								gatsbyImageData(
									width: 1000
									quality: 90
									placeholder: BLURRED
									formats: [AUTO, WEBP, AVIF]
								)
							}
						}
						Location_name
					}
				}
			}
		}
	}
`;
